import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Building2, Mail, Phone, MapPin, 
  ShoppingBag, FileText, MessageSquare, TrendingUp,
  Calendar, Euro, Package, Star, Eye, ExternalLink, Paperclip, Download, UserCheck,
  Clock, CheckCircle, Circle, Bell
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import CustomerTimeline from './CustomerTimeline';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('anagrafica');
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [followUpChannel, setFollowUpChannel] = useState('');
  
  useEffect(() => {
    if (adminToken && id) {
      loadCustomerData();
    }
  }, [id, adminToken]);

  useEffect(() => {
    if ((activeTab === 'note' || activeTab === 'timeline') && adminToken) {
      loadNotes();
    }
  }, [activeTab, adminToken]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/summary`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setCustomerData(result.data);
      } else {
        alert('Cliente non trovato');
        navigate('/admin/customers');
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      alert('Errore nel caricamento del cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadNotes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/notes`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setNotes(result.data || []);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      setAddingNote(true);
      
      // Se ci sono allegati, usa l'endpoint con multipart/form-data
      if (attachments.length > 0) {
        const formData = new FormData();
        formData.append('text', newNote);
        
        // Aggiungi follow-up data se presenti
        if (followUpDate) {
          formData.append('followUpDate', followUpDate);
        }
        if (followUpTime) {
          formData.append('followUpTime', followUpTime);
        }
        if (followUpChannel) {
          formData.append('followUpChannel', followUpChannel);
        }
        
        attachments.forEach((file) => {
          formData.append('files', file);
        });
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/notes/with-attachments`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${adminToken}`
            },
            body: formData
          }
        );

        if (response.ok) {
          toast({
            title: "Nota aggiunta",
            description: `Nota con ${attachments.length} allegato/i aggiunta con successo`
          });
          setNewNote('');
          setAttachments([]);
          setFollowUpDate('');
          setFollowUpTime('');
          setFollowUpChannel('');
          loadNotes();
        } else {
          throw new Error('Errore nell\'aggiunta della nota');
        }
      } else {
        // Senza allegati, usa l'endpoint standard
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/notes`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${adminToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              text: newNote,
              followUpDate: followUpDate || null,
              followUpTime: followUpTime || null,
              followUpChannel: followUpChannel || null
            })
          }
        );

        if (response.ok) {
          toast({
            title: "Nota aggiunta",
            description: "Nota aggiunta con successo"
          });
          setNewNote('');
          setFollowUpDate('');
          setFollowUpTime('');
          setFollowUpChannel('');
          loadNotes();
        } else {
          throw new Error('Errore nell\'aggiunta della nota');
        }
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: "Errore",
        description: error.message || 'Errore nell\'aggiunta della nota',
        variant: "destructive"
      });
    } finally {
      setAddingNote(false);
    }
  };

  const handleAssignToMe = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/assign`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Cliente assegnato",
          description: "Cliente assegnato a te con successo"
        });
        loadCustomerData();
      } else {
        throw new Error('Errore nell\'assegnazione');
      }
    } catch (error) {
      console.error('Error assigning customer:', error);
      toast({
        title: "Errore",
        description: 'Errore nell\'assegnazione del cliente',
        variant: "destructive"
      });
    }
  };

  const handleChangeStatus = async (newStatus) => {
    if (!window.confirm(`Cambiare status a "${newStatus}"?`)) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (response.ok) {
        loadCustomerData();
      }
    } catch (error) {
      console.error('Error changing status:', error);
    }
  };

  const handleToggleFollowUp = async (noteId, currentStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/notes/${noteId}/followup`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isCompleted: !currentStatus })
        }
      );

      if (response.ok) {
        toast({
          title: "Follow-up aggiornato",
          description: !currentStatus ? "Follow-up completato" : "Follow-up riaperto"
        });
        loadNotes();
      } else {
        throw new Error('Errore nell\'aggiornamento del follow-up');
      }
    } catch (error) {
      console.error('Error updating follow-up:', error);
      toast({
        title: "Errore",
        description: 'Errore nell\'aggiornamento del follow-up',
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT');
  };

  const getStatusBadge = (status) => {
    const badges = {
      lead: { color: 'bg-blue-100 text-blue-800', label: 'Lead' },
      prospect: { color: 'bg-yellow-100 text-yellow-800', label: 'Prospect' },
      customer: { color: 'bg-green-100 text-green-800', label: 'Cliente' },
      vip: { color: 'bg-purple-100 text-purple-800', label: 'VIP' }
    };
    const badge = badges[status] || badges.prospect;
    return <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>{badge.label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento cliente...</p>
        </div>
      </div>
    );
  }

  if (!customerData) return null;

  const { customer, stats, recent_orders, recent_quotes } = customerData;

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Link
          to="/admin/customers"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Torna alla lista clienti
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                {customer.userType === 'business' ? (
                  <Building2 className="w-8 h-8 text-purple-600" />
                ) : (
                  <User className="w-8 h-8 text-purple-600" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {customer.businessInfo?.companyName || `${customer.firstName} ${customer.lastName}`}
                </h1>
                <p className="text-gray-600">{customer.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusBadge(customer.customerStatus)}
                  {customer.leadSource && (
                    <span className="text-xs text-gray-500">
                      Fonte: {customer.leadSource}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Assigned To Info */}
              {customer.assignedToEmail && (
                <div className="text-sm bg-blue-50 px-3 py-2 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {customer.assignedToEmail.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="text-xs text-blue-600">Assegnato a</div>
                      <div className="font-semibold text-blue-900 capitalize">
                        {customer.assignedToEmail.split('@')[0].replace(/[._-]/g, ' ')}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Assign To Me Button */}
              <button
                onClick={handleAssignToMe}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
              >
                <UserCheck className="w-4 h-4" />
                {customer.assignedToEmail ? 'Riassegna a me' : 'Assegna a me'}
              </button>
              
              {/* Change Status Dropdown */}
              <select
                value={customer.customerStatus}
                onChange={(e) => handleChangeStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="lead">Lead</option>
                <option value="prospect">Prospect</option>
                <option value="customer">Cliente</option>
                <option value="vip">VIP</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Fatturato Totale</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_spent)}</p>
              </div>
              <Euro className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ordini Totali</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_orders}</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Preventivi</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.quotes_accepted}/{stats.total_quotes}
                </p>
                <p className="text-xs text-gray-500">accettati/totali</p>
              </div>
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Valore Medio</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.avg_order_value)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-600" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'anagrafica', label: 'Anagrafica', icon: User },
                { id: 'ordini', label: 'Ordini', icon: ShoppingBag },
                { id: 'preventivi', label: 'Preventivi', icon: FileText },
                { id: 'note', label: 'Note CRM', icon: MessageSquare },
                { id: 'timeline', label: 'Timeline', icon: Clock }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Tab: Anagrafica */}
            {activeTab === 'anagrafica' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Dati Base */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Informazioni Base</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Email</p>
                          <p className="text-gray-900">{customer.email}</p>
                        </div>
                      </div>
                      {customer.phone && (
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-500">Telefono</p>
                            <p className="text-gray-900">{customer.phone}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Registrato il</p>
                          <p className="text-gray-900">{formatDate(customer.createdAt)}</p>
                        </div>
                      </div>
                      {stats.last_order_date && (
                        <div className="flex items-start gap-3">
                          <Package className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-500">Ultimo Ordine</p>
                            <p className="text-gray-900">{formatDate(stats.last_order_date)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dati Business/Privato */}
                  <div>
                    {customer.userType === 'business' && customer.businessInfo ? (
                      <>
                        <h3 className="text-lg font-semibold mb-4">Dati Azienda</h3>
                        <div className="space-y-2">
                          {customer.businessInfo.companyName && (
                            <div>
                              <p className="text-sm text-gray-500">Ragione Sociale</p>
                              <p className="text-gray-900">{customer.businessInfo.companyName}</p>
                            </div>
                          )}
                          {customer.businessInfo.vatNumber && (
                            <div>
                              <p className="text-sm text-gray-500">P.IVA</p>
                              <p className="text-gray-900">{customer.businessInfo.vatNumber}</p>
                            </div>
                          )}
                          {customer.businessInfo.fiscalCode && (
                            <div>
                              <p className="text-sm text-gray-500">Codice Fiscale</p>
                              <p className="text-gray-900">{customer.businessInfo.fiscalCode}</p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-4">Dati Personali</h3>
                        <div className="space-y-2">
                          <div>
                            <p className="text-sm text-gray-500">Nome Completo</p>
                            <p className="text-gray-900">{customer.firstName} {customer.lastName}</p>
                          </div>
                          {customer.fiscalCode && (
                            <div>
                              <p className="text-sm text-gray-500">Codice Fiscale</p>
                              <p className="text-gray-900">{customer.fiscalCode}</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Interessi Lead - dal form landing */}
                {customer.leadInfo && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-5">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-amber-800">
                      <TrendingUp className="w-5 h-5" />
                      Interessi dal Form Landing
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Liquori di interesse */}
                      {customer.leadInfo.liquorTypes && customer.leadInfo.liquorTypes.length > 0 && (
                        <div>
                          <p className="text-sm text-amber-700 font-medium mb-2">🍾 Liquori di Interesse</p>
                          <div className="flex flex-wrap gap-2">
                            {customer.leadInfo.liquorTypes.map((type, idx) => (
                              <span key={idx} className="px-3 py-1 bg-white border border-amber-300 text-amber-800 rounded-full text-sm capitalize shadow-sm">
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Formato bottiglia */}
                      {customer.leadInfo.bottleFormat && (
                        <div>
                          <p className="text-sm text-amber-700 font-medium mb-2">📏 Formato Richiesto</p>
                          <span className="px-3 py-1 bg-white border border-amber-300 text-amber-800 rounded-full text-sm shadow-sm">
                            {customer.leadInfo.bottleFormat}
                          </span>
                        </div>
                      )}
                      
                      {/* Quantità */}
                      {customer.leadInfo.quantityRange && (
                        <div>
                          <p className="text-sm text-amber-700 font-medium mb-2">📦 Quantità Desiderata</p>
                          <span className="px-3 py-1 bg-white border border-amber-300 text-amber-800 rounded-full text-sm font-semibold shadow-sm">
                            {customer.leadInfo.quantityRange} bottiglie
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Messaggio iniziale */}
                    {customer.leadInfo.initialMessage && (
                      <div className="mt-4 pt-4 border-t border-amber-200">
                        <p className="text-sm text-amber-700 font-medium mb-2">💬 Messaggio Iniziale</p>
                        <p className="text-gray-700 bg-white p-3 rounded-lg border border-amber-200 italic">
                          "{customer.leadInfo.initialMessage}"
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Indirizzo */}
                {customer.addresses?.billingAddress && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Indirizzo di Fatturazione
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-900">
                        {customer.addresses.billingAddress.street}<br />
                        {customer.addresses.billingAddress.postalCode} {customer.addresses.billingAddress.city} ({customer.addresses.billingAddress.province})<br />
                        {customer.addresses.billingAddress.country}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Ordini */}
            {activeTab === 'ordini' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Ultimi Ordini</h3>
                {recent_orders.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nessun ordine ancora</p>
                ) : (
                  <div className="space-y-3">
                    {recent_orders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Ordine #{order.orderNumber || order.id}</p>
                            <p className="text-sm text-gray-500">{formatDate(order.createdAt)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">{formatCurrency(order.total)}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.status}
                            </span>
                          </div>
                          <Link
                            to={`/admin/orders/${order.id}`}
                            className="ml-4 p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Vedi dettaglio ordine"
                          >
                            <Eye className="w-5 h-5" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Preventivi */}
            {activeTab === 'preventivi' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Ultimi Preventivi</h3>
                {recent_quotes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nessun preventivo ancora</p>
                ) : (
                  <div className="space-y-3">
                    {recent_quotes.map((quote) => (
                      <div key={quote.id} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">Preventivo #{quote.quote_number}</p>
                              {quote.source === 'cart_request' && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                  Da Carrello
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{formatDate(quote.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">{formatCurrency(quote.total_amount)}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              quote.status === 'converted_to_order' ? 'bg-purple-100 text-purple-800' :
                              quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                              quote.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              quote.status === 'modification_requested' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {({
                                pending: 'In Attesa',
                                sent: 'Inviato',
                                accepted: 'Accettato',
                                rejected: 'Rifiutato',
                                modification_requested: 'Modifica Richiesta',
                                converted_to_order: 'Convertito in Ordine',
                                expired: 'Scaduto',
                              })[quote.status] || quote.status}
                            </span>
                          </div>
                          <Link
                            to={`/admin/quotes/${quote.id}`}
                            className="ml-4 p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Vedi dettaglio preventivo"
                          >
                            <Eye className="w-5 h-5" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Note */}
            {activeTab === 'note' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Note CRM</h3>
                
                {/* Add Note Form */}
                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Aggiungi una nota sul cliente..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  
                  {/* Follow-up Section */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Programma Follow-up (opzionale)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-blue-800 mb-1">Data</label>
                        <input
                          type="date"
                          value={followUpDate}
                          onChange={(e) => setFollowUpDate(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-blue-800 mb-1">Ora</label>
                        <input
                          type="time"
                          value={followUpTime}
                          onChange={(e) => setFollowUpTime(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-blue-800 mb-1">Canale</label>
                        <select
                          value={followUpChannel}
                          onChange={(e) => setFollowUpChannel(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Seleziona...</option>
                          <option value="phone">📞 Telefono</option>
                          <option value="email">📧 Email</option>
                          <option value="whatsapp">💬 WhatsApp</option>
                          <option value="meeting">🤝 Meeting</option>
                          <option value="other">📝 Altro</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Attachments Upload */}
                  <div className="mt-3">
                    <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 w-fit">
                      <Paperclip className="w-4 h-4 mr-2 text-gray-600" />
                      <span className="text-sm text-gray-600">Allega file</span>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => setAttachments([...attachments, ...Array.from(e.target.files)])}
                        className="hidden"
                      />
                    </label>
                    
                    {/* Attachments Preview */}
                    {attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-white px-3 py-2 rounded border text-sm">
                            <div className="flex items-center gap-2">
                              <Paperclip className="w-3 h-3 text-gray-400" />
                              <span className="text-gray-700">{file.name}</span>
                              <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <button
                              onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Rimuovi
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {addingNote ? 'Aggiunta...' : 'Aggiungi Nota'}
                    {attachments.length > 0 && (
                      <span className="text-xs bg-purple-700 px-2 py-0.5 rounded-full">
                        {attachments.length} file
                      </span>
                    )}
                  </button>
                </div>

                {/* Notes List */}
                {notes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nessuna nota ancora</p>
                ) : (
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <div key={note.id} className={`border-l-4 ${note.followUpDate && !note.isFollowUpCompleted ? 'border-orange-500 bg-orange-50' : 'border-purple-600 bg-white'} p-4 rounded-r-lg shadow-sm`}>
                        <p className="text-gray-900 mb-2 whitespace-pre-wrap">{note.text}</p>
                        
                        {/* Follow-up Info */}
                        {note.followUpDate && (
                          <div className={`mt-3 p-3 rounded-lg ${note.isFollowUpCompleted ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Bell className={`w-4 h-4 ${note.isFollowUpCompleted ? 'text-green-600' : 'text-yellow-600'}`} />
                                  <span className={`text-sm font-semibold ${note.isFollowUpCompleted ? 'text-green-800' : 'text-yellow-800'}`}>
                                    Follow-up {note.isFollowUpCompleted ? 'Completato' : 'Programmato'}
                                  </span>
                                </div>
                                <div className="space-y-1 text-sm text-gray-700">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-3.5 h-3.5 text-gray-500" />
                                    <span>
                                      {new Date(note.followUpDate).toLocaleDateString('it-IT', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  {note.followUpTime && (
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-3.5 h-3.5 text-gray-500" />
                                      <span>{note.followUpTime}</span>
                                    </div>
                                  )}
                                  {note.followUpChannel && (
                                    <div className="flex items-center gap-2">
                                      <MessageSquare className="w-3.5 h-3.5 text-gray-500" />
                                      <span className="capitalize">
                                        {note.followUpChannel === 'phone' && '📞 Telefono'}
                                        {note.followUpChannel === 'email' && '📧 Email'}
                                        {note.followUpChannel === 'whatsapp' && '💬 WhatsApp'}
                                        {note.followUpChannel === 'meeting' && '🤝 Meeting'}
                                        {note.followUpChannel === 'other' && '📝 Altro'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => handleToggleFollowUp(note.id, note.isFollowUpCompleted)}
                                className={`ml-3 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                  note.isFollowUpCompleted
                                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                {note.isFollowUpCompleted ? (
                                  <>
                                    <Circle className="w-4 h-4" />
                                    Riapri
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4" />
                                    Completa
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Attachments */}
                        {note.attachments && note.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-600">Allegati:</p>
                            {note.attachments.map((attachment, idx) => (
                              <a
                                key={idx}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-2 rounded"
                              >
                                <Download className="w-4 h-4" />
                                <span>{attachment.filename}</span>
                                <span className="text-xs text-gray-500">
                                  ({(attachment.size / 1024).toFixed(1)} KB)
                                </span>
                              </a>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                          <span>{note.created_by}</span>
                          <span>•</span>
                          <span>{formatDateTime(note.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Timeline (P2 — cronologia unificata) */}
            {activeTab === 'timeline' && (
              <CustomerTimeline customerId={id} adminToken={adminToken} customerData={customerData} notes={notes} />
            )}
          </div>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default CustomerDetail;
